import React, { useEffect, useRef, useState } from "react";
import { Camera, Circle, Mic, Monitor, Pause, Play, RefreshCw, Square, X } from "lucide-react";

const MODES = [{ id: "camera", label: "Camara", Icon: Camera }, { id: "audio", label: "Audio", Icon: Mic }, { id: "screen", label: "Pantalla / pestana", Icon: Monitor }];
const MIME_TYPES = {
  video: ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"],
  audio: ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm", "audio/mp4"],
};
const styles = {
  root: { display: "grid", gap: 10, minWidth: 0, paddingBlock: 12, borderTop: "1px solid #3f3f46", color: "inherit", fontSize: 13, letterSpacing: 0 },
  row: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, minWidth: 0 },
  field: { display: "grid", gap: 4, flex: "1 1 160px", minWidth: 0 },
  select: { width: "100%", minWidth: 0, padding: "6px 8px", color: "#f4f4f5", background: "#18181b", border: "1px solid #52525b", borderRadius: 4 },
  button: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, minHeight: 34, padding: "6px 10px", border: "1px solid #52525b", borderRadius: 4, color: "#f4f4f5", background: "#27272a", fontSize: 13, whiteSpace: "normal" },
  message: { margin: 0, overflowWrap: "anywhere" },
};

function supportedMime(hasVideo) {
  const Recorder = window.MediaRecorder;
  if (typeof Recorder !== "function" || typeof Recorder.isTypeSupported !== "function") return "";
  return MIME_TYPES[hasVideo ? "video" : "audio"].find(type => Recorder.isTypeSupported(type)) || "";
}

function stopTracks(stream) {
  stream?.getTracks().forEach(track => track.stop());
}

function releaseMedia(session) {
  if (session.released) return;
  session.released = true;
  clearInterval(session.timer);
  if (session.frame != null) window.cancelAnimationFrame(session.frame);
  session.listeners?.forEach(([track, listener]) => track.removeEventListener("ended", listener));
  stopTracks(session.stream);
  session.source?.disconnect();
  session.analyser?.disconnect();
  if (session.context && session.context.state !== "closed") {
    Promise.resolve(session.context.close()).catch(() => {});
  }
}

function discardSession(session) {
  session.cancelled = true;
  if (session.recorder) {
    session.recorder.ondataavailable = null;
    session.recorder.onstop = null;
    session.recorder.onerror = null;
    if (session.recorder.state !== "inactive") {
      try { session.recorder.stop(); } catch { /* A device can end during cleanup. */ }
    }
  }
  releaseMedia(session);
  session.chunks = [];
}

function captureError(error) {
  switch (error?.name) {
    case "NotAllowedError": case "PermissionDeniedError": case "SecurityError":
      return "Permiso denegado. Autoriza la captura en el navegador para volver a intentar.";
    case "NotFoundError": case "DevicesNotFoundError":
      return "No se encontraron dispositivos o fuentes de captura disponibles.";
    case "OverconstrainedError":
      return "La entrada seleccionada ya no esta disponible. Selecciona otra entrada.";
    case "NotReadableError": case "TrackStartError":
      return "No se pudo abrir la entrada. Comprueba si otra aplicacion la esta usando.";
    case "NotSupportedError":
      return "El navegador no admite un formato de grabacion compatible.";
    default:
      return "No se pudo iniciar la captura. Revisa los permisos y la entrada seleccionada.";
  }
}

function timecode(milliseconds) {
  const seconds = Math.floor(milliseconds / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function CreatorCapture({ onFile, disabled = false, maxBytes = 128 * 1024 * 1024, maxSeconds = 1800 }) {
  const [mode, setMode] = useState("camera");
  const [withInput, setWithInput] = useState(true);
  const [systemAudio, setSystemAudio] = useState(true);
  const [audioId, setAudioId] = useState("");
  const [cameraId, setCameraId] = useState("");
  const [devices, setDevices] = useState([]);
  const [phase, setPhase] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [meterAvailable, setMeterAvailable] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const sessionRef = useRef(null);
  const previewRef = useRef(null);
  const mountedRef = useRef(false);
  const permissionRef = useRef(false);
  const onFileRef = useRef(onFile);
  onFileRef.current = onFile;

  const mediaDevices = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
  const hasInput = mode === "audio" || (mode === "camera" && withInput);
  const canRecord = Boolean(supportedMime(mode !== "audio"));
  const apiAvailable = typeof (mode === "screen" ? mediaDevices?.getDisplayMedia : mediaDevices?.getUserMedia) === "function";
  const busy = ["requesting", "recording", "paused", "stopping", "saving"].includes(phase);
  const locked = disabled || busy || Boolean(pendingFile);

  useEffect(() => {
    mountedRef.current = true;
    const refreshDevices = async () => {
      if (!permissionRef.current || !mediaDevices?.enumerateDevices) return;
      try {
        const available = await mediaDevices.enumerateDevices();
        if (mountedRef.current) setDevices(available.filter(device => ["audioinput", "videoinput"].includes(device.kind)));
      } catch { /* Device labels are optional; default inputs still work. */ }
    };
    mediaDevices?.addEventListener?.("devicechange", refreshDevices);
    return () => {
      mountedRef.current = false;
      mediaDevices?.removeEventListener?.("devicechange", refreshDevices);
      if (sessionRef.current) discardSession(sessionRef.current);
      sessionRef.current = null;
    };
  }, [mediaDevices]);

  useEffect(() => {
    const preview = previewRef.current;
    if (preview) preview.srcObject = sessionRef.current?.stream || null;
    return () => { if (preview) preview.srcObject = null; };
  }, [phase]);

  function release(session) {
    releaseMedia(session);
    if (previewRef.current) previewRef.current.srcObject = null;
    if (mountedRef.current) { setLevel(0); setMeterAvailable(false); }
  }

  function fail(session, message) {
    discardSession(session);
    release(session);
    if (sessionRef.current === session) sessionRef.current = null;
    if (mountedRef.current) { setError(message); setPhase("idle"); }
  }

  async function addFile(file) {
    if (!mountedRef.current) return;
    setPendingFile(file);
    setPhase("saving");
    setError("");
    try {
      await onFileRef.current(file);
      if (mountedRef.current) {
        setPendingFile(null);
        setResult(file.captureWithoutAudio ? "Grabacion agregada. La fuente no proporciono audio del sistema." : "Grabacion agregada al proyecto.");
      }
    } catch {
      if (mountedRef.current) setError("No se pudo agregar la grabacion al proyecto. Puedes reintentar.");
    } finally {
      if (mountedRef.current) setPhase("idle");
    }
  }

  function finish(session) {
    if (session.cancelled || session.finished || !mountedRef.current) return;
    session.finished = true;
    release(session);
    sessionRef.current = null;
    const type = session.recorder.mimeType || session.chunks.find(chunk => chunk.type)?.type || session.mime;
    const blob = new Blob(session.chunks, { type });
    session.chunks = [];
    if (!blob.size) {
      setError("La grabacion esta vacia. Vuelve a intentarlo.");
      setPhase("idle");
      return;
    }
    const extension = type.includes("mp4") ? (session.mode === "audio" ? "m4a" : "mp4") : type.includes("ogg") ? "ogg" : "webm";
    const file = new File([blob], `captura-${session.mode}-${Date.now()}.${extension}`, { type });
    file.captureWithoutAudio = session.mode === "screen" && session.withoutAudio;
    addFile(file);
  }

  function stop(session = sessionRef.current) {
    if (!session || session.stopping || session.cancelled) return;
    if (!session.recorder || session.recorder.state === "inactive") {
      discardSession(session);
      release(session);
      sessionRef.current = null;
      setPhase("idle");
      return;
    }
    session.stopping = true;
    setPhase("stopping");
    try {
      session.recorder.stop();
      release(session);
    } catch { fail(session, "No se pudo finalizar la grabacion. Vuelve a intentarlo."); }
  }

  function startMeter(session) {
    if (!session.stream.getAudioTracks().length) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) { setNotice("El medidor de entrada no esta disponible en este navegador."); return; }
    try {
      session.context = new AudioContext();
      session.analyser = session.context.createAnalyser();
      session.analyser.fftSize = 256;
      session.source = session.context.createMediaStreamSource(session.stream);
      // Analyser-only graph: the input is never connected to speaker output.
      session.source.connect(session.analyser);
      const samples = new Uint8Array(session.analyser.fftSize);
      let lastUpdate = -Infinity;
      const measure = now => {
        if (session.released || session.cancelled) return;
        if (now - lastUpdate >= 100) {
          session.analyser.getByteTimeDomainData(samples);
          const rms = Math.sqrt(samples.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0) / samples.length);
          setLevel(Math.min(1, rms));
          lastUpdate = now;
        }
        session.frame = window.requestAnimationFrame(measure);
      };
      const begin = () => {
        if (!mountedRef.current || session.released || session.cancelled) return;
        setMeterAvailable(true);
        session.frame = window.requestAnimationFrame(measure);
      };
      Promise.resolve(session.context.resume()).then(begin).catch(() => {
        if (mountedRef.current && !session.released) setNotice("No se pudo activar el medidor de entrada.");
      });
    } catch { setNotice("No se pudo activar el medidor de entrada."); }
  }

  async function activate() {
    if (locked || sessionRef.current || !canRecord || !apiAvailable) return;
    const session = { mode, chunks: [], bytes: 0, listeners: [], elapsed: 0 };
    sessionRef.current = session;
    setError(""); setNotice(""); setResult(""); setElapsed(0); setPhase("requesting");
    try {
      // Screen permission must be requested directly from this user gesture.
      const stream = mode === "screen"
        ? await mediaDevices.getDisplayMedia({ video: true, audio: systemAudio, systemAudio: systemAudio ? "include" : "exclude" })
        : await mediaDevices.getUserMedia({
          video: mode === "camera" ? { ...(cameraId ? { deviceId: { exact: cameraId } } : {}), width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } : false,
          audio: hasInput ? { ...(audioId ? { deviceId: { exact: audioId } } : {}), echoCancellation: false, noiseSuppression: false, autoGainControl: false } : false,
        });
      if (!mountedRef.current || session.cancelled || sessionRef.current !== session) { stopTracks(stream); return; }
      session.stream = stream;
      const liveAudio = stream.getAudioTracks().filter(track => track.readyState === "live");
      const liveVideo = stream.getVideoTracks().filter(track => track.readyState === "live");
      session.withoutAudio = !liveAudio.length;
      if (mode !== "audio" && !liveVideo.length) { fail(session, "La fuente no entrego una pista de video disponible."); return; }
      if (hasInput && !liveAudio.length) { fail(session, "La entrada no entrego una pista de audio disponible."); return; }
      if (mode === "screen" && systemAudio && !liveAudio.length) {
        setNotice("No se recibio audio del sistema. Se grabara solo video; el audio depende del navegador, del sistema y de la fuente compartida.");
      }
      session.mime = supportedMime(liveVideo.length > 0);
      if (!session.mime) { fail(session, "El navegador no admite un formato de grabacion compatible."); return; }
      session.recorder = new window.MediaRecorder(stream, { mimeType: session.mime });
      session.recorder.ondataavailable = event => {
        if (session.cancelled || !event.data?.size) return;
        session.bytes += event.data.size;
        if (session.bytes > maxBytes) {
          fail(session, "La grabacion excedio el limite de memoria. Graba una toma mas corta.");
          return;
        }
        session.chunks.push(event.data);
        if (session.bytes >= maxBytes * .9 && !session.stopping) {
          setNotice("Se alcanzo el limite de captura; finalizando la toma.");
          stop(session);
        }
      };
      session.recorder.onstop = () => finish(session);
      session.recorder.onerror = () => fail(session, "La grabacion fallo. Comprueba la entrada y vuelve a intentarlo.");
      stream.getTracks().forEach(track => {
        const ended = () => {
          if (session.cancelled || session.released) return;
          if (session.recorder.state === "inactive") setNotice("La fuente de captura se desconecto.");
          stop(session);
        };
        track.addEventListener("ended", ended);
        session.listeners.push([track, ended]);
      });
      if (mode !== "screen") {
        permissionRef.current = true;
        if (mediaDevices.enumerateDevices) {
          try {
            const available = await mediaDevices.enumerateDevices();
            if (mountedRef.current && !session.cancelled && !session.released) {
              setDevices(available.filter(device => ["audioinput", "videoinput"].includes(device.kind)));
              if (liveAudio[0]?.getSettings) setAudioId(liveAudio[0].getSettings().deviceId || "");
              if (liveVideo[0]?.getSettings) setCameraId(liveVideo[0].getSettings().deviceId || "");
            }
          } catch { setNotice("No se pudo actualizar la lista de entradas. Se usara la entrada autorizada."); }
        }
      }
      if (!mountedRef.current || session.cancelled || session.released) return;
      startMeter(session);
      setPhase("ready");
    } catch (failure) {
      if (mountedRef.current && !session.cancelled) fail(session, captureError(failure));
    }
  }

  function record() {
    const session = sessionRef.current;
    if (disabled || phase !== "ready" || !session || session.recorder.state !== "inactive") return;
    try {
      session.recorder.start(250);
      session.started = performance.now();
      session.timer = setInterval(() => {
        if (!session.released) {
          const milliseconds = session.elapsed + (session.started == null ? 0 : performance.now() - session.started);
          setElapsed(milliseconds);
          if (milliseconds >= maxSeconds * 1000) {
            setNotice("Se alcanzo la duracion maxima de captura; finalizando la toma.");
            stop(session);
          }
        }
      }, 200);
      setPhase("recording");
    } catch (failure) { fail(session, captureError(failure)); }
  }

  function pause() {
    const session = sessionRef.current;
    if (!session || !["recording", "paused"].includes(phase)) return;
    try {
      if (session.recorder.state === "paused") {
        session.recorder.resume(); session.started = performance.now(); setPhase("recording");
      } else {
        session.recorder.pause(); session.elapsed += performance.now() - session.started; session.started = null;
        setElapsed(session.elapsed); setPhase("paused");
      }
    } catch { fail(session, "No se pudo pausar o reanudar la grabacion."); }
  }

  function changeInput(change) {
    if (locked) return;
    if (sessionRef.current) { discardSession(sessionRef.current); release(sessionRef.current); sessionRef.current = null; }
    change(); setPhase("idle"); setNotice(""); setError(""); setResult(""); setElapsed(0);
  }

  const button = (label, Icon, action, isDisabled, testId, extra = {}) => (
    <button type="button" title={label} aria-label={label} data-testid={testId} onClick={action} disabled={isDisabled}
      style={{ ...styles.button, opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? "default" : "pointer" }} {...extra}>
      <Icon size={15} aria-hidden="true" />{label}
    </button>
  );
  const deviceSelect = (kind, label, value, change, testId) => (
    <label style={styles.field}>{label}
      <select aria-label={label} data-testid={testId} value={value} disabled={locked} style={styles.select}
        onChange={event => changeInput(() => change(event.target.value))}>
        <option value="">Predeterminada</option>
        {devices.filter(device => device.kind === kind && device.deviceId).map((device, index) => (
          <option key={device.deviceId} value={device.deviceId}>{device.label || `${label} ${index + 1}`}</option>
        ))}
      </select>
    </label>
  );
  const recorder = sessionRef.current?.recorder;
  const pauseSupported = typeof recorder?.pause === "function" && typeof recorder?.resume === "function";
  const status = { idle: result || "Listo para activar", requesting: "Esperando permiso...", ready: "Entrada lista", recording: "Grabando", paused: "En pausa", stopping: "Finalizando...", saving: "Agregando al proyecto..." }[phase];

  return (
    <section aria-label="Captura" data-testid="creator-capture" style={styles.root}>
      <div style={{ ...styles.row, justifyContent: "space-between" }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Grabar</h3>
        <output role="timer" aria-label="Tiempo grabado" style={{ fontFamily: "monospace", fontVariantNumeric: "tabular-nums", minWidth: "5ch" }}>{timecode(elapsed)}</output>
      </div>
      <div role="group" aria-label="Fuente de captura" style={styles.row}>
        {MODES.map(({ id, label, Icon }) => <React.Fragment key={id}>
          {button(label, Icon, () => changeInput(() => setMode(id)), locked, `capture-mode-${id}`, { "aria-pressed": mode === id, style: { ...styles.button, borderColor: mode === id ? "#a1a1aa" : "#52525b", background: mode === id ? "#3f3f46" : "#18181b", opacity: locked ? 0.5 : 1 } })}
        </React.Fragment>)}
      </div>
      <div style={styles.row}>
        {mode === "camera" && deviceSelect("videoinput", "Camara de entrada", cameraId, setCameraId, "capture-camera-input")}
        {hasInput && deviceSelect("audioinput", "Microfono / interfaz", audioId, setAudioId, "capture-audio-input")}
      </div>
      {mode === "camera" && <label style={styles.row}><input type="checkbox" data-testid="capture-with-audio" checked={withInput} disabled={locked} onChange={event => changeInput(() => setWithInput(event.target.checked))} />Grabar audio de entrada</label>}
      {mode === "screen" && <label style={styles.row}><input type="checkbox" data-testid="capture-system-audio" checked={systemAudio} disabled={locked} onChange={event => changeInput(() => setSystemAudio(event.target.checked))} />Solicitar audio del sistema / pestana</label>}
      {mode !== "audio" && ["ready", "recording", "paused"].includes(phase) && <video ref={previewRef} aria-label="Vista previa de captura" data-testid="capture-preview" autoPlay muted playsInline style={{ display: "block", width: "100%", maxHeight: 180, aspectRatio: "16 / 9", objectFit: "contain", background: "#09090b", borderRadius: 4 }} />}
      {meterAvailable && <label style={styles.row}>Nivel de entrada<meter aria-label="Nivel de entrada" min="0" max="1" value={level} style={{ flex: "1 1 100px", height: 12 }} /></label>}
      <div style={styles.row}>
        {phase === "idle" && !pendingFile && button(mode === "screen" ? "Compartir pantalla" : "Activar entradas", mode === "screen" ? Monitor : Mic, activate, locked || !canRecord || !apiAvailable, "capture-activate")}
        {phase === "ready" && button("Grabar", Circle, record, disabled, "capture-record")}
        {["recording", "paused"].includes(phase) && pauseSupported && button(phase === "paused" ? "Reanudar" : "Pausar", phase === "paused" ? Play : Pause, pause, false, "capture-pause")}
        {["ready", "requesting", "recording", "paused"].includes(phase) && button(["ready", "requesting"].includes(phase) ? "Cancelar captura" : "Detener y agregar", Square, () => stop(), false, "capture-stop")}
        {pendingFile && phase !== "saving" && <>
          {button("Reintentar agregar", RefreshCw, () => addFile(pendingFile), disabled, "capture-retry")}
          {button("Descartar grabacion", X, () => { setPendingFile(null); setError(""); }, disabled, "capture-discard")}
        </>}
      </div>
      <p role="status" style={styles.message}>{status}</p>
      {(!canRecord || !apiAvailable) && <p role="alert" style={styles.message}>{!canRecord ? "Este navegador no admite la grabacion de esta fuente en un formato compatible." : "Esta captura no esta disponible. Usa un navegador compatible y HTTPS o localhost."}</p>}
      {notice && <p role="status" style={{ ...styles.message, color: "#fcd34d" }}>{notice}</p>}
      {error && <p role="alert" style={{ ...styles.message, color: "#fca5a5" }}>{error}</p>}
    </section>
  );
}
